import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmulatorSession, SessionCancelledError, type SessionStatus } from '../emulator/EmulatorSession';
import { InputManager } from '../input/InputManager';
import { useStore } from '../state/store';
import { VirtualPad, clampPadSpeed, PAD_SPEED_MAX, PAD_SPEED_STEP } from './VirtualPad';
import { PauseMenu } from './PauseMenu';
import { PadLayoutEditor } from './PadLayoutEditor';
import { LoadProgressOverlay } from './LoadProgressOverlay';
import { isTouchDevice } from '../pwa/register';
import { APP_VERSION, appBuildId } from '../appVersion';
import { useOrientation, useWakeLock } from '../hooks/useFullscreen';
import type { RomMeta } from '../storage/romRepository';
import { FAST_FORWARD_GAMEPAD_BUTTON, FAST_FORWARD_KEY_CODE } from '../input/keymap';
import type { LoadProgress } from '../emulator/types';
import { formatPsxMemcardUsage, type PsxMemcardUsage } from '../storage/saveRepository';

export interface PlayerScreenProps {
  rom: RomMeta;
  onExit: () => void;
}

export function PlayerScreen({ rom, onExit }: PlayerScreenProps) {
  const settings = useStore((s) => s.settings);
  const toast = useStore((s) => s.toast);
  const updateSettings = useStore((s) => s.updateSettings);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sessionRef = useRef<EmulatorSession | null>(null);

  const [status, setStatus] = useState<SessionStatus>('idle');
  const [fps, setFps] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState<LoadProgress>({ percent: 0, label: '準備中' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [padVisible, setPadVisible] = useState(true);
  const [editingPad, setEditingPad] = useState(false);
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  const fastForwardRef = useRef(false);
  /** PS1: undefined = 未確認、null = コア未接続、Usage = 接続中 */
  const [memcardLive, setMemcardLive] = useState<PsxMemcardUsage | null | undefined>(undefined);
  const [discInfo, setDiscInfo] = useState<{ count: number; index: number; labels: string[] } | null>(
    null,
  );

  // ROM 切替時は速度を既定に戻す（セッションも作り直される）
  useEffect(() => {
    setSpeed(1);
    speedRef.current = 1;
    fastForwardRef.current = false;
  }, [rom.id]);

  const orientation = useOrientation();

  // InputManager はセッションを跨いで再生成しない（購読が切れるため）
  const input = useMemo(
    () =>
      new InputManager({
        blockOpposingDirections: settings.blockOpposingDirections,
        keymap: settings.keymap,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    input.setKeymap(settings.keymap);
  }, [input, settings.keymap]);

  useWakeLock(status === 'running');

  // PS1: コアの SAVE_RAM を定期的に覗き、未接続か空き不足かを HUD に出す
  useEffect(() => {
    if (rom.system !== 'psx') {
      setMemcardLive(undefined);
      return;
    }
    if (status !== 'running' && status !== 'paused') {
      setMemcardLive(undefined);
      return;
    }
    let cancelled = false;
    const tick = () => {
      void sessionRef.current?.peekPsxMemcard().then((u) => {
        if (!cancelled) setMemcardLive(u);
      });
    };
    tick();
    const id = window.setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [rom.system, status, rom.id]);

  /* ------------------------------------------------------------------ */
  /* セッションのライフサイクル                                          */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    const session = new EmulatorSession({
      rom,
      container,
      input,
      volume: settings.muted ? 0 : settings.volume,
      rewind: settings.rewindEnabled,
      integerScale: settings.integerScale,
      shader: settings.shader,
      useWorker: settings.useWorker,
      psxMemcardId: settings.activePsxMemcardId,
      ...(settings.preferredBackends[rom.system]
        ? { preferredBackendId: settings.preferredBackends[rom.system] }
        : {}),
      events: {
        onStatus: (s) => !disposed && setStatus(s),
        onFps: (f) => !disposed && setFps(f),
        onError: (e) => !disposed && setError(e.message),
        onProgress: (p) => !disposed && setLoadProgress(p),
        onRewindUnavailable: (reason) => !disposed && toast(reason, 'error'),
      },
    });
    sessionRef.current = session;

    setLoadProgress({ percent: 0, label: '準備中' });
    void session.start().catch((e: unknown) => {
      if (disposed || e instanceof SessionCancelledError) return;
      if (e instanceof DOMException && e.name === 'AbortError') return;
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : String(e));
    });

    const detachKeyboard = input.attachKeyboard();

    return () => {
      disposed = true;
      detachKeyboard();
      void session.dispose();
      sessionRef.current = null;
    };
    // rom.id が変わったときだけ作り直す。設定変更で再起動はしない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rom.id]);

  // 音量設定の変更を実行中のセッションへ反映
  useEffect(() => {
    sessionRef.current?.setVolume(settings.muted ? 0 : settings.volume);
  }, [settings.volume, settings.muted]);

  /* ------------------------------------------------------------------ */
  /* 操作ハンドラ                                                        */
  /* ------------------------------------------------------------------ */

  const openMenu = useCallback(() => {
    if (fastForwardRef.current) {
      fastForwardRef.current = false;
      sessionRef.current?.setSpeed(speedRef.current);
    }
    sessionRef.current?.setRewinding(false);
    sessionRef.current?.pause();
    setMenuOpen(true);
    void sessionRef.current?.getDiscInfo().then((info) => {
      if (info && info.count > 1) setDiscInfo(info);
      else if (rom.discs && rom.discs.length > 1) {
        setDiscInfo({
          count: rom.discs.length,
          index: info?.index ?? 0,
          labels: rom.discs.map((d) => d.label),
        });
      } else {
        setDiscInfo(null);
      }
    });
  }, [rom.discs]);

  const handleSwapDisc = useCallback(
    async (index: number) => {
      try {
        await sessionRef.current?.swapDisc(index);
        const info = await sessionRef.current?.getDiscInfo();
        if (info) setDiscInfo(info);
        toast(`${info?.labels[index] ?? `Disc ${index + 1}`} に切り替えました`, 'success');
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), 'error');
      }
    },
    [toast],
  );

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    sessionRef.current?.resume();
  }, []);

  const applySpeed = useCallback((next: number) => {
    const clamped = clampPadSpeed(next);
    setSpeed(clamped);
    speedRef.current = clamped;
    // 早送り中は最大速度を維持し、離したときに新しい基準速度へ戻す
    if (!fastForwardRef.current) {
      sessionRef.current?.setSpeed(clamped);
    }
  }, []);

  const setFastForwarding = useCallback((active: boolean) => {
    if (fastForwardRef.current === active) return;
    fastForwardRef.current = active;
    if (active) {
      sessionRef.current?.setSpeed(PAD_SPEED_MAX);
    } else {
      sessionRef.current?.setSpeed(speedRef.current);
    }
  }, []);

  const handleExit = useCallback(() => {
    // 読み込み中に dispose を await すると、Worker load 待ちで UI が戻れないことがある。
    // 先にライブラリへ戻り、破棄は裏で進める（unmount cleanup でも dispose される）。
    const session = sessionRef.current;
    sessionRef.current = null;
    onExit();
    void session?.dispose();
  }, [onExit]);

  const handleSaveState = useCallback(
    async (slot: number) => {
      try {
        await sessionRef.current?.createState(slot);
        toast(`スロット ${slot + 1} に保存しました`, 'success');
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), 'error');
      }
    },
    [toast],
  );

  const handleLoadState = useCallback(
    async (slot: number) => {
      try {
        await sessionRef.current?.restoreState(slot);
        toast(`スロット ${slot + 1} を読み込みました`, 'success');
        closeMenu();
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), 'error');
      }
    },
    [toast, closeMenu],
  );

  // PC / パッド向けショートカット:
  // ESC メニュー、R 巻き戻し、Tab 早送り（押しっぱなし）
  useEffect(() => {
    const blocked = () => menuOpen || editingPad;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (status === 'loading' || error) {
          void handleExit();
          return;
        }
        if (menuOpen) closeMenu();
        else if (!editingPad) openMenu();
        return;
      }
      if (blocked()) return;
      if (e.code === 'KeyR' && settings.rewindEnabled && !e.repeat) {
        e.preventDefault();
        sessionRef.current?.setRewinding(true);
      }
      if (e.code === FAST_FORWARD_KEY_CODE && !e.repeat) {
        e.preventDefault();
        setFastForwarding(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'KeyR') sessionRef.current?.setRewinding(false);
      if (e.code === FAST_FORWARD_KEY_CODE) setFastForwarding(false);
    };
    const onBlur = () => {
      sessionRef.current?.setRewinding(false);
      setFastForwarding(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [
    menuOpen,
    editingPad,
    openMenu,
    closeMenu,
    settings.rewindEnabled,
    setFastForwarding,
    status,
    error,
    handleExit,
  ]);

  // ゲームパッド R3 押しっぱなしで早送り（ゲーム入力には渡さない）
  useEffect(() => {
    let raf = 0;
    let wasPressed = false;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (menuOpen || editingPad) {
        if (wasPressed) {
          wasPressed = false;
          setFastForwarding(false);
        }
        return;
      }

      const pads = navigator.getGamepads?.() ?? [];
      let pressed = false;
      for (const pad of pads) {
        if (!pad?.connected) continue;
        const btn = pad.buttons[FAST_FORWARD_GAMEPAD_BUTTON];
        if (btn && (btn.pressed || btn.value > 0.5)) {
          pressed = true;
          break;
        }
      }

      if (pressed !== wasPressed) {
        wasPressed = pressed;
        setFastForwarding(pressed);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setFastForwarding(false);
    };
  }, [menuOpen, editingPad, setFastForwarding]);

  // パッドの「表示」と「レイアウト上の場所取り」は分けて扱う。
  // メニューを開いたときにパッドが消えて画面が伸縮すると、閉じたときに
  // 表示位置が飛んでしまい、当たり判定の再計測も必要になる。
  const padLayoutActive =
    padVisible &&
    (settings.showVirtualPad === 'always' ||
      (settings.showVirtualPad === 'auto' && isTouchDevice() && input.connectedGamepads === 0));
  const showPad = padLayoutActive && !menuOpen && !editingPad;

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      {/* iOS ステータスバー帯。縦持ちは --portrait-top-gap で少し伸ばし、フロスト帯から画素を外す。 */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-30 bg-black"
        style={{ height: 'calc(var(--safe-top) + var(--portrait-top-gap) + 2px)' }}
        aria-hidden
      />
      {/* エミュレータ画面。パッドの下に敷く。 */}
      <div
        ref={containerRef}
        className="screen-frame absolute inset-0 z-0"
        style={{
          // 縦持ち+パッド: 速度系はパッド内なので上余白はメニュー行分だけ
          // 横持ち/パッド無しタッチ: 速度 HUD が上にあるので余白を多めに
          paddingTop: isTouchDevice()
            ? orientation === 'portrait' && padLayoutActive
              ? 'calc(var(--safe-top) + var(--portrait-top-gap) + 2.5rem)'
              : 'calc(var(--safe-top) + var(--portrait-top-gap) + 3.25rem)'
            : 'var(--portrait-top-gap)',
          // 縦持ちではパッドと重ならないよう下に余白（速度バー分を少し足す）
          paddingBottom: orientation === 'portrait' && padLayoutActive ? '46vh' : 0,
        }}
      />

      {status === 'loading' && (
        <LoadProgressOverlay
          title={rom.title}
          percent={loadProgress.percent}
          label={loadProgress.label}
          onCancel={() => void handleExit()}
        />
      )}

      {error && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/90 p-4">
          <div className="card flex max-h-[85vh] w-full max-w-md flex-col p-5">
            <h2 className="shrink-0 text-sm font-semibold text-red-400">起動できませんでした</h2>
            {/*
              コアの出力とファイル一覧を丸ごと出すので長くなる。
              実機で原因を追えるよう、スクロールと選択・コピーを許す。
              ここだけ select-text にしているのは、他の場所では
              iOS の文字選択が誤爆して操作の邪魔になるため。
            */}
            <pre className="mt-2 min-h-0 flex-1 select-text overflow-auto whitespace-pre-wrap break-words font-mono text-[0.65rem] leading-relaxed text-slate-300">
              {error}
            </pre>
            <div className="mt-4 flex shrink-0 gap-2">
              {typeof navigator !== 'undefined' && navigator.clipboard && (
                <button
                  type="button"
                  className="btn-ghost flex-1"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(error)
                      .then(() => toast('エラー内容をコピーしました', 'success'))
                      .catch(() => toast('コピーできませんでした', 'error'));
                  }}
                >
                  コピー
                </button>
              )}
              <button type="button" className="btn-ghost flex-1" onClick={() => void handleExit()}>
                ライブラリに戻る
              </button>
            </div>
          </div>
        </div>
      )}

      {(settings.showFps || input.connectedGamepads > 0 || rom.system === 'psx') && (
        <div className="top-safe-hud pointer-events-none absolute left-[max(0.75rem,env(safe-area-inset-left))] z-20 flex select-none flex-col items-start gap-1 text-[0.65rem] text-white/50 [-webkit-touch-callout:none]">
          <div className="flex items-center gap-2">
            {settings.showFps && status === 'running' && (
              <span className="rounded bg-black/55 px-2 py-1 tabular-nums">{fps.toFixed(1)} fps</span>
            )}
            {input.connectedGamepads > 0 && (
              <span
                className="rounded bg-black/55 px-2 py-1"
                aria-label={`ゲームパッド ${input.connectedGamepads}`}
              >
                <span aria-hidden="true">🎮</span> {input.connectedGamepads}
              </span>
            )}
          </div>
          {rom.system === 'psx' && (
            <span
              className={`max-w-[calc(100vw-1.5rem-env(safe-area-inset-left)-env(safe-area-inset-right))] truncate rounded bg-black/55 px-2 py-1 ${
                memcardLive === null
                  ? 'text-red-300/90'
                  : memcardLive && memcardLive.formatted && memcardLive.freeBlocks === 0
                    ? 'text-amber-300/90'
                    : 'text-white/60'
              }`}
            >
              {settings.psxMemcards.find((c) => c.id === settings.activePsxMemcardId)?.name ??
                'メモリーカード'}
              {memcardLive === undefined
                ? ''
                : memcardLive === null
                  ? ' · 未接続'
                  : ` · ${formatPsxMemcardUsage(memcardLive)}`}
              <span className="text-white/35">
                {' '}
                · v{APP_VERSION} · {appBuildId()}
              </span>
            </span>
          )}
        </div>
      )}

      {showPad && (
        <VirtualPad
          system={rom.system}
          input={input}
          analog={rom.system === 'psx' || rom.system === 'psp'}
          opacity={settings.padOpacity}
          scale={settings.padScale}
          orientation={orientation}
          layout={settings.padLayouts[orientation]}
          directionalStyle={settings.padDirectional}
          onMenu={openMenu}
          speed={speed}
          onSpeedDown={() => applySpeed(speed - PAD_SPEED_STEP)}
          onSpeedUp={() => applySpeed(speed + PAD_SPEED_STEP)}
          onFastForward={setFastForwarding}
          {...(settings.rewindEnabled
            ? { onRewind: (active: boolean) => sessionRef.current?.setRewinding(active) }
            : {})}
        />
      )}

      {/* パッド非表示時にもメニュー / 早送りへ行けるようにする */}
      {!showPad && !menuOpen && !editingPad && (
        <div className="top-safe-hud absolute right-[max(0.75rem,env(safe-area-inset-right))] z-20 flex items-center gap-1.5">
          <button
            type="button"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              setFastForwarding(true);
            }}
            onPointerUp={() => setFastForwarding(false)}
            onPointerCancel={() => setFastForwarding(false)}
            onLostPointerCapture={() => setFastForwarding(false)}
            className="inline-flex h-9 min-w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 px-2.5 text-white/70 transition-colors active:border-accent active:bg-accent/60 active:text-black"
            aria-label="早送り"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden fill="currentColor">
              <path d="M3.2 6.4c0-.85.95-1.35 1.65-.85l8.1 5.1c.65.4.65 1.35 0 1.75l-8.1 5.1c-.7.45-1.65-.05-1.65-.9V6.4z" />
              <path d="M12.1 6.4c0-.85.95-1.35 1.65-.85l8.1 5.1c.65.4.65 1.35 0 1.75l-8.1 5.1c-.7.45-1.65-.05-1.65-.9V6.4z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={openMenu}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/20 bg-black/55 px-3 text-xs text-white/70"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden fill="currentColor">
              <path d="M4 7h16v1.8H4V7zm0 4.1h16v1.8H4v-1.8zm0 4.1h16V17H4v-1.8z" />
            </svg>
            メニュー
          </button>
        </div>
      )}

      {editingPad && (
        <PadLayoutEditor
          system={rom.system}
          analog={rom.system === 'psx' || rom.system === 'psp'}
          orientation={orientation}
          padScale={settings.padScale}
          padOpacity={settings.padOpacity}
          directionalStyle={settings.padDirectional}
          initial={settings.padLayouts[orientation]}
          onCancel={() => {
            setEditingPad(false);
            sessionRef.current?.resume();
          }}
          onSave={(next) => {
            void updateSettings({
              padLayouts: { ...settings.padLayouts, [orientation]: next },
            });
            setEditingPad(false);
            sessionRef.current?.resume();
            toast('パッドの配置を保存しました', 'success');
          }}
        />
      )}

      {menuOpen && (
        <PauseMenu
          rom={rom}
          onResume={closeMenu}
          onExit={() => void handleExit()}
          onReset={() => {
            sessionRef.current?.reset();
            closeMenu();
          }}
          onSaveState={handleSaveState}
          onLoadState={handleLoadState}
          onTogglePad={() => setPadVisible((v) => !v)}
          padVisible={padVisible}
          speed={speed}
          onSpeed={applySpeed}
          cheatsSupported={sessionRef.current?.cheatsSupported ?? false}
          onCheatsChanged={() => void sessionRef.current?.applyCheats()}
          onEditPad={() => {
            setMenuOpen(false);
            setEditingPad(true);
          }}
          onScreenshot={async () => {
            const blob = await sessionRef.current?.screenshot();
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${rom.title}.png`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          discInfo={discInfo}
          onSwapDisc={handleSwapDisc}
        />
      )}
    </div>
  );
}
